import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { useKPIEventsInfinite } from "PFCore/hooks/queries/kpi/use_kpi_events";
import { Id } from "PFCore/types";
import { KPIStatus as KPIStatusEnum, KPIUnit } from "PFCore/types/kpi";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { KPIActionTile, KPIEventTile } from "./kpi_event_tile";
import css from "./kpi_events_list.module.scss";

type KPIEventsListProps = {
  engagementId: Id;
  KPIId: Id;
  KPIName?: string;
  KPIUnit?: KPIUnit;
  KPIStatus?: KPIStatusEnum;
  hideLoader: boolean;
  onEventsCountChange?: (count: number) => void;
};

export const KPIEventsList = ({
  engagementId,
  KPIId,
  KPIName,
  KPIUnit,
  KPIStatus,
  hideLoader,
  onEventsCountChange
}: KPIEventsListProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "KPIsReviewSidepanel" });
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useKPIEventsInfinite(
    engagementId,
    { indicatorId: KPIId, perPage: 10 }
  );

  const events = data?.pages.flatMap((page) => page.entries) || [];

  useEffect(() => {
    onEventsCountChange?.(events.length);
  }, [events.length, onEventsCountChange]);

  return (
    <Stack gap={null}>
      {isFetching && !isFetchingNextPage && !hideLoader && (
        <LoadingDots circlesEnabled circleSize="sm" centered className={css.eventsLoader} />
      )}
      <Stack gap={null}>
        {/* Container to allow CSS top/bottom border hiding */}
        {!!KPIName && (
          <KPIActionTile
            key={KPIId}
            KPIId={KPIId}
            KPIName={KPIName}
            KPIStatus={KPIStatus}
            engagementId={engagementId}
          />
        )}
        {!!KPIUnit && events.map((event) => <KPIEventTile key={event.id} event={event} KPIUnit={KPIUnit} />)}
      </Stack>
      {hasNextPage && !isFetchingNextPage && (
        <Inline className={css.loadMore} center>
          <Button kind="text" onClick={() => fetchNextPage()}>
            {t("loadMore")}
          </Button>
        </Inline>
      )}
      {isFetchingNextPage && (
        <LoadingDots circlesEnabled circleSize="sm" centered className={css.nextPageLoader} />
      )}
    </Stack>
  );
};
